import { couponApi } from "@api";
import {
  ComponentPagination,
  SearchFilter,
  SelectedFilter,
  SharedButton,
  StyledListPage,
  ComponentHeader,
  StyledInnerListPage,
  DownloadIcon,
} from "@components";

import { familyFilter, enumFilter, initPage } from "@configs";
import { IPagination, IRedeemCoupon } from "@interfaces";
import { RedeemTable } from "@modules";
import { setLoading } from "@redux";
import { useNotify } from "@utils";
import { Tooltip } from "antd";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

export const PageRedeemList = () => {
  // hook
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { success, error } = useNotify();

  // state
  const [page, setPage] = useState<IPagination>(initPage);
  const [total, setTotal] = useState<number>(0);
  const [data, setData] = useState<IRedeemCoupon[]>([]);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState<string>("");
  const [date, setDate] = useState<string[]>([]);

  useEffect(() => {
    fetchCoupon();
  }, [page, text, date]);

  const fetchCoupon = async () => {
    try {
      dispatch(setLoading(true));
      const res = await couponApi.redeemCoupon({
        page: page.page,
        limit: page.limit,
        searchColumn: filters,
        search: text,
        startDate: date[0],
        endDate: date[1],
      });
      setData(res.data?.data);
      setTotal(res.data?.nTotal);
      success(t("message.fetch.success"));
    } catch (err: any) {
      error(t("message.fetch.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };
  const handleDateFilter = (dates: null | (Dayjs | null)[], dateStrings: string[]) => {
    setDate(dateStrings);
  };
  const handlePagination = (current: number, size: number) => {
    setPage({ ...page, page: current, limit: size });
  };
  const changeFilter = (value: string) => {
    setFilters(value);
    searchText("");
  };
  const searchText = (value: string) => setText(value);
  const handleClearSearch = () => {
    setText("");
    setFilters("");
  };
  const handleClearDate = () => setDate([]);

  const handleDownload = async () => {
    try {
      const { data } = await couponApi.exportExcel({
        searchColumn: filters,
        search: text,
        startDate: date[0],
        endDate: date[1],
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: `text/csv` }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "export redeem coupon");
      link.click();
      window.URL.revokeObjectURL(url);
      success(t("message.download.success"));
    } catch (err: any) {
      error(t("message.download.fail"));
      throw new Error(err);
    }
  };

  const button = (
    <Tooltip title={t("page.download_excel")}>
      <SharedButton
        type="primary"
        ghost
        icon={<DownloadIcon />}
        size="middle"
        onClick={handleDownload}
      >
        {t("page.download")}
      </SharedButton>
    </Tooltip>
  );

  return (
    <>
      <ComponentHeader title={t("page.coupon_management")} />
      <StyledListPage>
        <StyledInnerListPage>
          <SearchFilter
            actionBtn={button}
            filter={familyFilter}
            callbackFun={changeFilter}
            onDateChange={handleDateFilter}
            searchText={searchText}
            searchInputPlaceholder={t("page.search_user_by")}
            selectedOption={filters}
          />
          <SelectedFilter
            handleClearSearch={handleClearSearch}
            handleClearDate={handleClearDate}
            selected={filters}
            formDate={date[0]}
            toDate={date[1]}
          />
          <RedeemTable data={data} callback={fetchCoupon} />
          <ComponentPagination total={total} page={page.page} changePage={handlePagination} />
        </StyledInnerListPage>
      </StyledListPage>
    </>
  );
};
